/**
 * RawJsonTab Component
 *
 * Displays formatted JSON with syntax highlighting and copy functionality.
 */

import React, { useState } from 'react'
import { Button, Space, Typography, message, Card, Segmented, Tooltip } from 'antd'
import {
  CopyOutlined,
  CheckOutlined,
  ExpandOutlined,
  CompressOutlined,
  DownloadOutlined
} from '@ant-design/icons'
import type { TabProps } from '../types'

const { Text } = Typography

/**
 * JSON syntax highlighting colors
 */
const SYNTAX_COLORS = {
  key: '#881391',      // Purple for keys
  string: '#0451a5',   // Blue for strings
  number: '#098658',   // Green for numbers
  boolean: '#0000ff',  // Blue for booleans
  null: '#8c8c8c',     // Gray for null
  bracket: '#000000',  // Black for brackets
  colon: '#000000',    // Black for colons
  comma: '#000000'     // Black for commas
}

/**
 * Highlight JSON syntax
 */
function highlightJson(json: string): React.ReactNode[] {
  const result: React.ReactNode[] = []
  let i = 0
  let key = 0

  // Simple tokenizer
  while (i < json.length) {
    const char = json[i]

    // Whitespace and newlines
    if (char === ' ' || char === '\n' || char === '\t' || char === '\r') {
      result.push(char)
      i++
      continue
    }

    // Brackets and structural characters
    if (char === '{' || char === '}' || char === '[' || char === ']') {
      result.push(
        <span key={key++} style={{ color: SYNTAX_COLORS.bracket, fontWeight: 600 }}>
          {char}
        </span>
      )
      i++
      continue
    }

    if (char === ':') {
      result.push(
        <span key={key++} style={{ color: SYNTAX_COLORS.colon }}>
          {char}
        </span>
      )
      i++
      continue
    }

    if (char === ',') {
      result.push(
        <span key={key++} style={{ color: SYNTAX_COLORS.comma }}>
          {char}
        </span>
      )
      i++
      continue
    }

    // String (key or value)
    if (char === '"') {
      let str = '"'
      i++
      while (i < json.length && json[i] !== '"') {
        if (json[i] === '\\' && i + 1 < json.length) {
          str += json[i] + json[i + 1]
          i += 2
        } else {
          str += json[i]
          i++
        }
      }
      str += '"'
      i++

      // Determine if this is a key (followed by :) or a value
      let j = i
      while (j < json.length && (json[j] === ' ' || json[j] === '\n' || json[j] === '\t')) {
        j++
      }
      const isKey = json[j] === ':'

      result.push(
        <span key={key++} style={{ color: isKey ? SYNTAX_COLORS.key : SYNTAX_COLORS.string }}>
          {str}
        </span>
      )
      continue
    }

    // Number
    if (char === '-' || (char >= '0' && char <= '9')) {
      let num = ''
      while (i < json.length && (json[i] === '-' || json[i] === '.' || json[i] === 'e' || json[i] === 'E' || json[i] === '+' || (json[i] >= '0' && json[i] <= '9'))) {
        num += json[i]
        i++
      }
      result.push(
        <span key={key++} style={{ color: SYNTAX_COLORS.number }}>
          {num}
        </span>
      )
      continue
    }

    // Boolean or null
    if (json.substring(i, i + 4) === 'true') {
      result.push(
        <span key={key++} style={{ color: SYNTAX_COLORS.boolean, fontWeight: 600 }}>
          true
        </span>
      )
      i += 4
      continue
    }

    if (json.substring(i, i + 5) === 'false') {
      result.push(
        <span key={key++} style={{ color: SYNTAX_COLORS.boolean, fontWeight: 600 }}>
          false
        </span>
      )
      i += 5
      continue
    }

    if (json.substring(i, i + 4) === 'null') {
      result.push(
        <span key={key++} style={{ color: SYNTAX_COLORS.null, fontStyle: 'italic' }}>
          null
        </span>
      )
      i += 4
      continue
    }

    // Default: just add the character
    result.push(char)
    i++
  }

  return result
}

export function RawJsonTab({ metadata }: TabProps): React.ReactElement {
  const [copied, setCopied] = useState(false)
  const [indentSize, setIndentSize] = useState<number>(2)
  const [expanded, setExpanded] = useState(true)

  // Format JSON
  const formattedJson = expanded
    ? JSON.stringify(metadata, null, indentSize)
    : JSON.stringify(metadata)

  // Copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedJson)
      setCopied(true)
      message.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      message.error('Failed to copy')
    }
  }

  // Download as file
  const handleDownload = () => {
    const blob = new Blob([formattedJson], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'metadata.json'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    message.success('Downloaded metadata.json')
  }

  // Calculate stats
  const lineCount = formattedJson.split('\n').length
  const charCount = formattedJson.length
  const fieldCount = Object.keys(metadata.fields || {}).length

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <Space>
          <Tooltip title={expanded ? 'Minify' : 'Expand'}>
            <Button
              icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => setExpanded(!expanded)}
              size="small"
            >
              {expanded ? 'Minify' : 'Expand'}
            </Button>
          </Tooltip>
          {expanded && (
            <Segmented
              size="small"
              value={indentSize}
              onChange={(value) => setIndentSize(value as number)}
              options={[
                { label: '2 spaces', value: 2 },
                { label: '4 spaces', value: 4 }
              ]}
            />
          )}
        </Space>
        <Space>
          <Button
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={handleCopy}
            type={copied ? 'primary' : 'default'}
            size="small"
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            size="small"
          >
            Download
          </Button>
        </Space>
      </div>

      {/* JSON viewer */}
      <Card
        size="small"
        style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        bodyStyle={{
          flex: 1,
          overflow: 'auto',
          padding: 0
        }}
      >
        <pre
          style={{
            margin: 0,
            padding: '12px',
            fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', 'Droid Sans Mono', 'Source Code Pro', monospace",
            fontSize: '12px',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: '#fafafa',
            minHeight: '100%'
          }}
        >
          {expanded ? highlightJson(formattedJson) : formattedJson}
        </pre>
      </Card>

      {/* Stats footer */}
      <div style={{
        marginTop: '8px',
        padding: '8px 12px',
        background: '#fafafa',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#8c8c8c',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
          <span>{fieldCount} fields</span>
          <span>{lineCount} lines</span>
          <span>{charCount.toLocaleString()} characters</span>
        </Space>
        <Text type="secondary">
          {(charCount / 1024).toFixed(2)} KB
        </Text>
      </div>
    </div>
  )
}

export default RawJsonTab
